import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, CheckCircle2 } from "lucide-react";
import { API, waLink, MESSAGES } from "@/data/site";
import { INDUSTRIES } from "@/data/industries";
import { useLang } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const LeadForm = () => {
  const t = useLang().t;
  const [form, setForm] = useState({
    name: "",
    business_name: "",
    phone: "",
    city: "",
    business_category: "",
    has_website: "no",
    requirement: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        business_name: form.business_name.trim(),
        phone: form.phone.trim(),
        city: form.city.trim(),
        business_category: form.business_category,
        has_website: form.has_website === "yes",
        requirement: form.requirement.trim(),
      });
      setDone(true);
      toast.success("Request received. We will contact you shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please WhatsApp us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="lead-form-success" className="card-site p-8 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-[#25D366]" />
        <h3 className="mt-4 font-heading text-2xl font-bold text-[#3E2723]">
          {t.form.success}
        </h3>
        <p className="mt-2 text-[#5D4037]">{t.form.successSub}</p>
        <a
          href={waLink(MESSAGES.home)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="lead-form-success-whatsapp"
          className="btn-whatsapp mt-6"
        >
          <MessageCircle className="h-4 w-4" /> {t.form.successCta}
        </a>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="lead-form"
      className="card-site p-6 sm:p-8 space-y-5"
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="lead-name">{t.form.name}</Label>
          <Input id="lead-name" data-testid="lead-input-name" required value={form.name} onChange={set("name")} placeholder={t.form.namePh} className="bg-white" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lead-business">{t.form.business}</Label>
          <Input id="lead-business" data-testid="lead-input-business" required value={form.business_name} onChange={set("business_name")} placeholder={t.form.businessPh} className="bg-white" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lead-phone">{t.form.phone}</Label>
          <Input id="lead-phone" data-testid="lead-input-phone" required value={form.phone} onChange={set("phone")} placeholder={t.form.phonePh} className="bg-white" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lead-city">{t.form.city}</Label>
          <Input id="lead-city" data-testid="lead-input-city" required value={form.city} onChange={set("city")} placeholder={t.form.cityPh} className="bg-white" />
        </div>
      </div>

      <div className="space-y-2">
        <Label>{t.form.category}</Label>
        <Select value={form.business_category} onValueChange={set("business_category")} required>
          <SelectTrigger data-testid="lead-select-category" className="bg-white">
            <SelectValue placeholder={t.form.categoryPh} />
          </SelectTrigger>
          <SelectContent>
            {INDUSTRIES.map((i, idx) => (
              <SelectItem key={i.slug} value={i.name} data-testid={`lead-category-${i.slug}`}>
                {t.industries[idx].name}
              </SelectItem>
            ))}
            <SelectItem value="Other" data-testid="lead-category-other">{t.form.other}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>{t.form.hasWebsite}</Label>
        <RadioGroup
          value={form.has_website}
          onValueChange={set("has_website")}
          className="flex gap-6"
          data-testid="lead-radio-website"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="yes" id="lead-web-yes" data-testid="lead-radio-yes" />
            <Label htmlFor="lead-web-yes" className="font-normal">{t.form.yes}</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="no" id="lead-web-no" data-testid="lead-radio-no" />
            <Label htmlFor="lead-web-no" className="font-normal">{t.form.no}</Label>
          </div>
        </RadioGroup>
      </div>

      <div className="space-y-2">
        <Label htmlFor="lead-requirement">{t.form.need}</Label>
        <Textarea
          id="lead-requirement"
          data-testid="lead-input-requirement"
          value={form.requirement}
          onChange={set("requirement")}
          placeholder={t.form.needPh}
          rows={4}
          className="bg-white"
        />
      </div>

      <button type="submit" data-testid="lead-form-submit-button" disabled={submitting} className="btn-primary w-full !py-3.5 text-base disabled:opacity-60">
        {submitting ? t.form.sending : t.form.submit}
      </button>
      <p className="text-center text-xs text-[#8D6E63]">
        {t.form.privacy}
      </p>
    </form>
  );
};

export default LeadForm;
